import { useEffect, useState, useCallback } from "react";
import { Loader2, Lock, ArrowRight } from "lucide-react";
import StaffHeader from "@/components/StaffHeader";
import InventorySubNav from "@/components/inventory/InventorySubNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function UsageEntry() {
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [date, setDate] = useState(todayIso());
  const [items, setItems] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [sortAlpha, setSortAlpha] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    api.get("/inventory/locations").then(({ data }) => {
      setLocations(data);
      if (data.length && !locationId) setLocationId(data[0].id);
    });
  }, [locationId]);

  const fetchItems = useCallback(async () => {
    if (!locationId) return;
    setItems(null);
    const { data } = await api.get("/inventory/usage-entry", { params: { location_id: locationId } });
    setItems(data);
    setQuantities({});
  }, [locationId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const sortedItems = items
    ? [...items].sort((a, b) => (sortAlpha ? a.name.localeCompare(b.name) : (a.sr_no ?? Infinity) - (b.sr_no ?? Infinity) || a.name.localeCompare(b.name)))
    : [];

  const filledCount = Object.values(quantities).filter((v) => v !== "" && v != null).length;
  const hasOverLimitRow = items?.some((item) => {
    const typed = quantities[item.item_id];
    return typed !== "" && typed != null && Number(typed) > item.available_stock;
  });

  const handleQuantityChange = (itemId, value) => {
    setQuantities((prev) => ({ ...prev, [itemId]: value }));
  };

  const handleSave = async () => {
    const entries = Object.entries(quantities)
      .filter(([, v]) => v !== "" && v != null && Number(v) > 0)
      .map(([item_id, v]) => ({ item_id, quantity: Number(v) }));
    if (entries.length === 0) return;
    setSaving(true);
    try {
      const { data } = await api.post("/inventory/usage-entry", { location_id: locationId, date, entries });
      toast({ title: "Usage saved", description: data.message });
      fetchItems();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = detail?.errors ? detail.errors.map((e) => e.error).join(" · ") : formatApiErrorDetail(detail);
      toast({ variant: "destructive", title: "Could not save usage", description: message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-amtm-bg">
      <StaffHeader />
      <div className="max-w-3xl mx-auto px-4 py-6 pb-32">
        <h1 className="font-display text-3xl text-amtm-text mb-2" data-testid="usage-entry-title">Daily Usage Entry</h1>
        <InventorySubNav />

        <div className="flex flex-wrap gap-4 mb-4 items-end">
          <div>
            <Label>Drawer</Label>
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger className="w-44 mt-1.5" data-testid="usage-entry-location-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5 h-9 w-40" data-testid="usage-entry-date-input" />
          </div>
          <Button
            type="button"
            variant={sortAlpha ? "default" : "outline"}
            onClick={() => setSortAlpha((v) => !v)}
            data-testid="usage-entry-sort-toggle"
            className={sortAlpha ? "bg-amtm-teal text-white h-9" : "h-9"}
          >
            {sortAlpha ? "Sorted A–Z" : "Sort A–Z"}
          </Button>
        </div>

        {items === null ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-amtm-teal" /></div>
        ) : sortedItems.length === 0 ? (
          <p className="text-amtm-textMuted text-center py-12" data-testid="usage-entry-empty-state">No items in this drawer yet. Receive stock first.</p>
        ) : (
          <div className="bg-amtm-surface border border-amtm-border rounded-xl divide-y divide-amtm-border" data-testid="usage-entry-list">
            {sortedItems.map((item) => {
              const typed = quantities[item.item_id];
              const typedNum = Number(typed);
              const overLimit = typed !== "" && typed != null && typedNum > item.available_stock;
              return (
                <div
                  key={item.item_id}
                  data-testid={`usage-entry-row-${item.item_id}`}
                  className={`flex items-center justify-between gap-3 p-3 flex-wrap ${overLimit ? "bg-amtm-error/10" : item.locked_expired ? "bg-amtm-textMuted/5" : ""}`}
                >
                  <div className="min-w-0">
                    <p className="font-semibold text-amtm-text">{item.name}</p>
                    <p className="text-xs text-amtm-textMuted">{item.company_name || "—"} · Exp {item.earliest_expiry || "—"} · Stock: {item.available_stock} {item.unit}</p>
                  </div>
                  {item.locked_expired ? (
                    <div className="flex items-center gap-1.5 text-amtm-error text-sm font-medium shrink-0" data-testid={`usage-entry-locked-${item.item_id}`}>
                      <Lock className="h-3.5 w-3.5" /> Expired
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 shrink-0">
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder="Used"
                        value={typed ?? ""}
                        onChange={(e) => handleQuantityChange(item.item_id, e.target.value)}
                        data-testid={`usage-entry-qty-input-${item.item_id}`}
                        className={`w-20 h-9 ${overLimit ? "border-amtm-error text-amtm-error" : ""}`}
                      />
                      {typed !== "" && typed != null && !isNaN(typedNum) && (
                        overLimit ? (
                          <span className="text-xs text-amtm-error font-medium" data-testid={`usage-entry-error-${item.item_id}`}>Only {item.available_stock} left</span>
                        ) : (
                          <span className="text-xs text-amtm-textMuted flex items-center gap-1"><ArrowRight className="h-3 w-3" /> {item.available_stock - typedNum} left</span>
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="fixed bottom-0 left-0 right-0 bg-amtm-surface border-t border-amtm-border p-4 flex justify-end">
          <div className="max-w-3xl mx-auto w-full flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving || filledCount === 0 || hasOverLimitRow}
              data-testid="usage-entry-save-button"
              className="bg-amtm-teal text-white hover:bg-amtm-tealHover"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Usage ({filledCount} item{filledCount === 1 ? "" : "s"})
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
